import { AttributesListItemMulti } from 'src/utils/createAttributesItem.ts';
import { ActuatorData, ValveData } from 'src/modules/checkout/CheckoutPageMapping.ts';
import { AttributesAliases } from 'src/generated/dict.ts';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { UserInput } from 'src/modules/user-input/types.ts';
import { LocaleValuesFragment } from 'src/generated/operations.ts';
import { UnitValue } from 'shared/projects/UnitValue.ts';

const createUnitValue = (v: string, u: string): UnitValue | undefined => (v.length > 0 ? [v, u] : undefined);
const createLocaleValueFragment = (v: string | null | undefined): LocaleValuesFragment | undefined => {
    if (v === undefined || v === null) {
        return undefined;
    }
    return { raw: [v], localized: [v] };
};

export function prepareExportAttributes(
    valve: ValveData,
    actuator: ActuatorData,
    userInputState: UserInput,
) {
    let attrs: AttributesListItemMulti[] = [];
    const product: ActuatorData = actuator || valve;
    const is6way = valve.pattern?.raw.join('').includes('6');

    let steamDiffs: AttributesListItemMulti[] = [];

    if ('kvs' in valve) {
        steamDiffs = [
            [MESSAGES.inlet, [createUnitValue(userInputState.inlet, userInputState.inlet_u)]],
            [MESSAGES.outlet, [createUnitValue(userInputState.outlet, userInputState.outlet_u)]],
        ];
    }

    if ('cv' in valve) {
        steamDiffs = [
            [MESSAGES.inlet, [createUnitValue(userInputState.inlet, userInputState.inlet_u)]],
        ];
    }

    const common: AttributesListItemMulti[] = [
        [MESSAGES.EffectiveDpv, createLocaleValueFragment(valve.calcEffectiveDpv)],
        [MESSAGES.EffectiveDpv1, createLocaleValueFragment(valve.calcEffectiveDpv6way1)],
        [MESSAGES.EffectiveDpv2, createLocaleValueFragment(valve.calcEffectiveDpv6way2)],
        ...steamDiffs,
        [MESSAGES.MinReqDpv, createLocaleValueFragment(is6way ? null : valve.calcMinReqDpv)],
        [MESSAGES.MinReqDpv1, createLocaleValueFragment(is6way ? valve.calcMinReqDpv : null)],
        [MESSAGES.MinReqDpv2, createLocaleValueFragment(valve.calcMinReqDpv2)],
        [MESSAGES.EffectivePV, createLocaleValueFragment(is6way ? null : valve.calcEffectivePV)],
        [MESSAGES.EffectivePV1, createLocaleValueFragment(is6way ? valve.calcEffectivePV1 : null)],
        [MESSAGES.EffectivePV2, createLocaleValueFragment(valve.calcEffectivePV2)],
        [MESSAGES.FlowVelocity, createLocaleValueFragment(is6way ? null : valve.calcFlowVelocity)],
        [MESSAGES.FlowVelocity1, createLocaleValueFragment(is6way ? valve.calcFlowVelocity : null)],
        [MESSAGES.FlowVelocity2, createLocaleValueFragment(valve.calcFlowVelocity2)],
        [AttributesAliases.nominalVoltage, product.nominalVoltage],
        [AttributesAliases.controlType, product.controlType],
        [AttributesAliases.runningTime, product.runningTime],
        [AttributesAliases.failSafeFunction, product.failSafeFunction],
    ];

    const clipPosKvs = valve.getClipPositionKvs
        ? { raw: [valve.getClipPositionKvs], localized: [valve.getClipPositionKvs] } : null;
    if ('kvs' in valve) {
        const kvs = clipPosKvs || valve.kvs;
        attrs = [
            [AttributesAliases.dn, valve.dn],
            [MESSAGES.v100, is6way ? null : [createUnitValue(userInputState.v100, userInputState.v100_u)]],
            [MESSAGES.v100_1, is6way ? [createUnitValue(userInputState.v100, userInputState.v100_u)] : null],
            [MESSAGES.v100_2, [createUnitValue(userInputState.v100_2, userInputState.v100_2_u)]],
            [MESSAGES.massFlow, [createUnitValue(userInputState.mass, userInputState.mass_u)]],
            [AttributesAliases.vnomls, valve.vnomm3h],
            [MESSAGES.KV, is6way ? null : [createUnitValue(userInputState.kvs, userInputState.kvs_u)]],
            [MESSAGES.KV_1, is6way ? [createUnitValue(userInputState.kvs, userInputState.kvs_u)] : null],
            [MESSAGES.KV_2, [createUnitValue(userInputState.kvs_2, userInputState.kvs_2_u)]],
            [AttributesAliases.kvmax, valve.kvmax],
            [AttributesAliases.kvs, kvs],
            [AttributesAliases.kvssequence1, valve.kvssequence1],
            [AttributesAliases.kvssequence2, valve.kvssequence2],
            ...common,
            [MESSAGES.clipPosition, createLocaleValueFragment(valve.getClipPosition || valve.getClipPositionPI)],
        ];
    }
    if ('cv' in valve) {
        const cv = clipPosKvs || valve.cv;
        attrs = [
            [AttributesAliases.bodySize, valve.bodySize],
            [MESSAGES.v100, is6way ? null : [createUnitValue(userInputState.v100, userInputState.v100_u)]],
            [MESSAGES.v100_1, is6way ? [createUnitValue(userInputState.v100, userInputState.v100_u)] : null],
            [MESSAGES.v100_2, [createUnitValue(userInputState.v100_2, userInputState.v100_2_u)]],
            [MESSAGES.massFlow, [createUnitValue(userInputState.mass, userInputState.mass_u)]],
            [AttributesAliases.vnomgpm, valve.vnomgpm],
            [MESSAGES.CV, is6way ? null : [createUnitValue(userInputState.kvs, userInputState.kvs_u)]],
            [MESSAGES.CV_1, is6way ? [createUnitValue(userInputState.kvs, userInputState.kvs_u)] : null],
            [MESSAGES.CV_2, [createUnitValue(userInputState.kvs_2, userInputState.kvs_2_u)]],
            [AttributesAliases.cv, cv],
            [AttributesAliases.cvsequence1, valve.cvsequence1],
            [AttributesAliases.cvsequence2, valve.cvsequence2],
            ...common,
            [MESSAGES.clipPosition, createLocaleValueFragment(valve.getClipPosition || valve.getClipPositionPI)],
        ];
    }
    return attrs;
}
